import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';

import { Box, Button, FormControl, FormControlLabel, Grid, Paper, Radio, RadioGroup, SpeedDial, SpeedDialAction, Tooltip, Typography } from "@mui/material";
import { IconComponent } from "@zippeditoolsjs/zippedi-icons";

import MapTypeToggle from "../../../tools/MapTypeToggle";
import PageLink from "../../../tools/PageLink";
import StoreMap from '../../../tools/StoreMap';
import { highlightedProductColor, mapBackgroundColor } from "../../../utils/colors";
import DownloadSection from "../DownloadSection";
import ObstacleModal from "./ObstacleModal";

export default function NavigationMap(props) {
  const {
    date,
    storeMap,
    taskType,
    setIsSidenavOpen,
    selectedStore,
    selectedStoreTaskType,
    selectedCategory,
    currentFloor,
    setCurrentFloor,
    openTimeline,
    setOpenTimeline,
    heightRef,
    isHoverActive = true,
    isCLickActive = true,
    canShowActions = true,
    highlightedBoxes = [],
    isZoomActive,
    poses,
    currentSessionObstaclesInfo,
    obstacles,
    visualizationMode: inputVisualizationMode = 'ds_nr_basic',
    taskData,
    getDigitalStorePdf,
    isPdfLoading,
    selectedClient,
    linkReceived,
    filteredTaskData,
    setSelectedAlert,
    getObstacleThumbnails,
    isLoadingObstacleThumbnails,
    obstacleThumbnails,
    navigationReportSummary,
    aislesInfo,
    isLoadingAislesInfo,
    mapType,
    setMapType,
    hasEnhancedMap,
    setSnackAlert,
  } = props;
  const { t } = useTranslation();

  const [isHoverOn, setIsHoverOn] = useState(false);
  const [categoriesOnMap, setCategoriesOnMap] = useState({});

  const [selectedObstacleIndex, setSelectedObstacleIndex] = useState(null);
  const [selectedElement, setSelectedElement] = useState({});
  const [isTooltipFixed, setIsTooltipFixed] = useState(false);

  // Speed dial states
  const [visualizationMode, setVisualizationMode] = useState(inputVisualizationMode);
  const [openSpeedDial, setOpenSpeedDial] = useState(false);
  const [openLabel, setOpenLabel] = useState(false);
  const [openFloor, setOpenFloor] = useState(false);
  const speedDialRef = useRef(null);
  const labelRef = useRef(null);
  const floorRef = useRef(null);

  const [isTooltipPined, setIsTooltipPined] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [drawerRet, setDrawerRet] = useState({});
  const [mouseInObs, setMouseInObs] = useState(false);

  const [highlightStyle, setHighlightStyle] = useState({
    height: '20px',
    width: '20px',
    top: 0,
    left: 0,
    backgroundColor: 'transparent',
    position: 'absolute',
    border: `2px ${highlightedProductColor()} solid`,
    opacity: 0,
    zIndex: -1,
    transition: 'opacity 0.2s',
  });

  // Hook that closes the speed dial or the filters depending on clicks outside of the passed ref
  useEffect(() => {
    // Close speed dial if clicked on outside of element
    function handleClickOutside(event) {
      if (labelRef.current && !labelRef.current.contains(event.target)) {
        setOpenLabel(false);
      } else if (floorRef.current && !floorRef.current.contains(event.target)) {
        setOpenFloor(false);
      } else if (!labelRef.current && !floorRef.current && speedDialRef.current && !speedDialRef.current.contains(event.target)) {
        setOpenSpeedDial(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [speedDialRef, labelRef, floorRef]);

  const moveTooltipCanvas = ({ mode, coord, isTooltipFixed = false }) => {
    const tooltipCanvas = document.getElementById('tooltipCanvas');
    const responsiveCanvas = document.getElementById('responsive-canvas');
    if (mode === 'moveOn') {
      setIsTooltipFixed(isTooltipFixed);
      const [xElement, yElement] = coord;
      // Scale the alert coordinates for the tooltip
      const width = responsiveCanvas.offsetWidth
      const height = responsiveCanvas.offsetHeight
      const original_width = storeMap?.map_info?.width
      const original_height = storeMap?.map_info?.height
      const scaled_x = xElement * width / original_width
      const scaled_y = yElement * height / original_height
      const [newLeft, newTop] = [`${scaled_x + 30}px`, `${scaled_y + 50}px`]; // + 30/50 so the tooltip is in the correct position
      tooltipCanvas.style.left = newLeft;
      tooltipCanvas.style.top = newTop;
      tooltipCanvas.style.opacity = '1';
      tooltipCanvas.style.zIndex = '1';
    } else if (mode === 'close') {
      setIsTooltipFixed(false);
      tooltipCanvas.style.opacity = '0';
      tooltipCanvas.style.zIndex = '-1';
    } else {
      tooltipCanvas.style.opacity = '0';
      tooltipCanvas.style.zIndex = '-1';
    }
  }

  const getMousePos = (event, parsedTaskData) => {
    const x = event.clientX,
      y = event.clientY;
    let notFound = true;
    for (let index = 0; index < parsedTaskData.obstacles.length; index++) {
      const obstacle = parsedTaskData.obstacles[index]
      const [x1Left, y1Left] = obstacle.px_left_down
      const [x1Right, y1Right] = obstacle.px_right_down
      const [x2Left, y2Left] = obstacle.px_right_up
      const [x2Right, y2Right] = obstacle.px_left_up

      const xMin = Math.min(x1Left, x1Right, x2Left, x2Right);
      const xMax = Math.max(x1Left, x1Right, x2Left, x2Right);
      const yMin = Math.min(y1Left, y1Right, y2Left, y2Right);
      const yMax = Math.max(y1Left, y1Right, y2Left, y2Right);

      const mouseIn = (xMin <= x && x <= xMax) && (yMin <= y && y <= yMax);

      if (mouseIn && (!isHoverOn || index !== selectedObstacleIndex)) {
        setMouseInObs(true);
        document.body.style.cursor = "pointer";
        if (index !== selectedObstacleIndex) {
          setSelectedObstacleIndex(index);
        }
        notFound = false;
        setIsHoverOn(true);
        obstacle["hovered"] = true
        setSelectedElement(obstacle);
        highlightElement(obstacle);
        moveTooltipCanvas({ mode: 'moveOn', coord: [x, y] });
        break;
      } else if (mouseIn && isHoverOn) {
        document.body.style.cursor = "pointer";
        notFound = false;
        break;
      }
    }
    if (notFound) {
      setMouseInObs(false);
      setSelectedElement({ ...selectedElement, hovered: false })
      if (!isTooltipPined) {
        setSelectedElement({ index: undefined, groupName: undefined, aisle_name: undefined, client_aisle_name: undefined, landmark: undefined, map_point: undefined, tramo: undefined, radius: undefined, completed: undefined, totalAlerts: undefined });
        if (!isTooltipFixed) { moveTooltipCanvas({}) };
        setIsHoverOn(false);
      }
      highlightElement(selectedElement, false);
      document.body.style.cursor = "default";
    }
  }

  const highlightElement = (element, isActive = true) => {
    if (isActive) {
      const responsiveCanvas = document.getElementById('responsive-canvas');
      // Scale the alert coordinates for the tooltip
      const width = responsiveCanvas.offsetWidth
      const height = responsiveCanvas.offsetHeight
      const original_width = storeMap?.map_info?.width
      const original_height = storeMap?.map_info?.height

      const centerX = (element.px_left_up[0] + element.px_right_up[0] + element.px_left_down[0] + element.px_right_down[0]) / 4;
      const centerY = (element.px_left_up[1] + element.px_right_up[1] + element.px_left_down[1] + element.px_right_down[1]) / 4;
      const scaled_x = (centerX * width) / original_width;
      const scaled_y = (centerY * height) / original_height;
      const [newLeft, newTop] = [scaled_x + 16.5, scaled_y + 56]; // + 30/50 so the tooltip is in the correct position

      let highlighterHeight = Math.sqrt(
        (element.px_left_up[0] - element.px_left_down[0]) * (element.px_left_up[0] - element.px_left_down[0]) +
        (element.px_left_up[1] - element.px_left_down[1]) * (element.px_left_up[1] - element.px_left_down[1])
      );

      const highlighterWidth = Math.sqrt(
        (element.px_right_up[0] - element.px_left_up[0]) * (element.px_right_up[0] - element.px_left_up[0]) +
        (element.px_right_up[1] - element.px_left_up[1]) * (element.px_right_up[1] - element.px_left_up[1])
      );

      const angleRad = Math.atan2(
        element.px_right_down[1] - element.px_left_down[1],
        element.px_right_down[0] - element.px_left_down[0]
      );
      const angleDeg = angleRad * (180 / Math.PI);

      const newStyle = {
        ...highlightStyle
        , left: `${newLeft}px`, top: `${newTop}px`
        , opacity: '1', zIndex: '1'
        , height: `${1.2 * ((highlighterHeight * height) / original_height)}px`, width: `${1.2 * ((highlighterWidth * width) / original_width)}px`
        , transform: `translate(-50%, -50%)  rotate(${angleDeg}deg)`
        , pointerEvents: 'none'
      }
      setHighlightStyle(newStyle);
    } else {
      setHighlightStyle({ ...highlightStyle, opacity: '0', zIndex: '-1' });
    }
  }

  const handleCanvasClick = (event, forceCloseTooltip = false) => {
    if (selectedElement?.hovered && !forceCloseTooltip) {
      document.body.style.cursor = "default";
      setIsTooltipPined(true);
      setIsModalOpen(true);
      setSelectedAlert(selectedElement);
      setTimeout(() => {
        highlightElement(selectedElement);
        const [xElement, yElement] = selectedElement.px_left_up;
        moveTooltipCanvas({ mode: 'moveOn', coord: [xElement, yElement] });
      }, 150);
    } else {
      setIsTooltipPined(false);
      setSelectedElement({ hovered: false, index: undefined, groupName: undefined, aisle_name: undefined, client_aisle_name: undefined, landmark: undefined, map_point: undefined, tramo: undefined, radius: undefined, completed: undefined, totalAlerts: undefined });
      setIsHoverOn(false);
      moveTooltipCanvas({});
      highlightElement(selectedElement, false);
    }
  }

  const handleCanvasHover = (event, drawerReturn) => {
    if (drawerReturn?.obstacles) {
      setDrawerRet(drawerReturn);
      getMousePos(event, drawerReturn);
    }
  }

  // Speed dial actions and handlers
  const actions = [
    { icon: <IconComponent iconName={'filter'} style={{ fontSize: '20px' }} />, name: t('cws_app.digital_store.navigation_filter', 'Navigation filter'), operation: 'label' },
    { icon: <IconComponent iconName={'map'} style={{ fontSize: '20px' }} />, name: t('cws_app.digital_store.change_floor', 'Change floor'), operation: 'floor' },
    { icon: <IconComponent iconName={'play-skip-forward'} style={{ fontSize: '20px' }} />, name: t('cws_app.digital_store.timeline', 'Timeline'), operation: 'timeline' },
    {
      icon: <div onClick={(e) => e.stopPropagation()}>
        <PageLink
          currentState={{
            date,
            selectedStore,
            selectedStoreTaskType,
            selectedCategory
          }}
          view="/cpg/digitalStore"
        />
      </div>,
      name: t('cws_app.general.copy_link', 'Copy link'), operation: 'link'
    },
    {
      icon: <div onClick={(e) => e.stopPropagation()}>
        {/* The navigation report only exports a PDF, so no Excel or .INV props */}
        <DownloadSection
          displayedColumns={storeMap?.columns}
          chain={selectedStore?.chain_name}
          filteredTaskData={filteredTaskData}

          client_id={selectedClient?.client_id}
          signed_logo={selectedClient?.signed_logo}

          inputDate={format(date, 'yyyy-MM-dd')}
          inputTaskType={selectedStoreTaskType?.task_name}
          inputStoreId={selectedStore?.store_id}

          getDigitalStorePdf={getDigitalStorePdf}
          isPdfLoading={isPdfLoading}

          linkReceived={linkReceived}

          storeMap={storeMap}
          navigationReportSummary={navigationReportSummary}
          aislesInfo={aislesInfo}
          isLoadingAislesInfo={isLoadingAislesInfo}
          setSnackAlert={setSnackAlert}
        />
      </div>,
      name: t('cws_app.general.download_pdf', 'Download PDF'), operation: 'download'
    }
  ];

  const handleSpeedDialOpen = () => setOpenSpeedDial(true);

  const handleSpeedDialActionClick = (e, operation) => {
    e.preventDefault();
    setOpenSpeedDial(true);
    switch (operation) {
      case 'label':
        setOpenLabel(!openLabel);
        break;
      case 'floor':
        setOpenFloor(!openFloor);
        break;
      case 'timeline':
        setOpenTimeline(!openTimeline);
        setVisualizationMode('ds_timeline');
        break;
      case 'link':
        e.stopPropagation();
        break;
      case 'download':
        e.stopPropagation();
        setOpenFloor(false);
        break;
      default:
        break;
    }
  }

  // Speed dial label filter
  const handleLabelChange = (event) => {
    setVisualizationMode(event?.target?.value)
    setOpenTimeline(false);
  }

  // Speed dial floor selection
  const handleFloorChange = (event) => {
    const currentFloorObj = storeMap.sessions_by_floor[Number(event?.target?.value)]
    setCurrentFloor(currentFloorObj)
  }

  const handleObstacleChange = (event, newValue) => {
    setSelectedObstacleIndex(newValue)
    if (drawerRet?.length !== 0) {
      const obstacle = drawerRet.obstacles[newValue]
      const [x1Left, y1Left] = obstacle.px_left_up
      moveTooltipCanvas({ mode: 'moveOn', coord: [x1Left, y1Left] });
      highlightElement(obstacle);
    }
  };

  const handleNavigationSummaryClick = (e, operation) => {
    setIsTooltipPined(false);
    setIsTooltipFixed(false);
    setIsSidenavOpen(true);
  }

  return (
    <Paper ref={heightRef} style={{ position: 'relative', width: '100%', height: 'fit-content', padding: '0 1em', backgroundColor: mapBackgroundColor() }}>
      {/* Speed dial and custom tooltips */}
      {openLabel &&
        <Paper ref={labelRef} sx={{ position: 'absolute', right: '4em', top: '4em', padding: 1, zIndex: 99, pointerEvents: 'auto' }}>
          <FormControl>
            <RadioGroup
              name="label-filter"
              defaultValue="ds_nr_basic"
              value={visualizationMode}
              onChange={handleLabelChange}
            >
              <FormControlLabel value="ds_nr_basic" control={<Radio size='small' />} label={t('cws_app.digital_store.all', 'All')} />
              <FormControlLabel value="ds_poses" control={<Radio size='small' />} label={t('cws_app.digital_store.route', 'Route')} />
              <FormControlLabel value="ds_nav_poses" control={<Radio size='small' />} label={t('cws_app.digital_store.nav_mode', 'NAV mode')} />
              <FormControlLabel value="ds_waf_poses" control={<Radio size='small' />} label={t('cws_app.digital_store.waf_mode', 'WAF mode')} />
              <FormControlLabel value="ds_obstacles" control={<Radio size='small' />} label={t('cws_app.digital_store.obstacles', 'Obstacles')} />
            </RadioGroup>
          </FormControl>
        </Paper>
      }
      {openFloor &&
        <Paper ref={floorRef} sx={{ position: 'absolute', right: '9em', top: '4em', padding: 1, zIndex: 99, pointerEvents: 'auto' }}>
          <FormControl>
            <RadioGroup
              aria-labelledby="floor-selection"
              name="floor-selection"
              value={currentFloor?.floor_number}
              onChange={handleFloorChange}
            >
              {Object.values(storeMap.sessions_by_floor).map(floor => {
                return (
                  <FormControlLabel value={floor.floor_number} key={floor.floor_number} control={<Radio size='small' />} label={
                    t('cws_app.digital_store.sel_floor_i', 'Floor {{floorIndex}}', { floorIndex: floor?.floor_number })
                  }
                  />
                )
              })}
            </RadioGroup>
          </FormControl>
        </Paper>
      }
      <Grid container direction='row' justifyContent='space-between' alignItems='flex-end' sx={{ transform: 'translateZ(0px)', flexGrow: 1, zIndex: 10, position: 'relative' }}>
        <Box sx={{ display: 'flex', mb: 1, ml: 1 }}>
          <MapTypeToggle mapType={mapType} setMapType={setMapType} hasEnhancedMap={hasEnhancedMap} />
        </Box>
        {canShowActions &&
          <Box display='flex' justifyContent='flex-end' alignItems='flex-end'>
            <SpeedDial
              ref={speedDialRef}
              ariaLabel="Map configurations"
              FabProps={{ size: 'small' }}
              icon={<IconComponent iconName={'settings'} style={{ fontSize: '20px' }} />}
              direction="left"
              open={openSpeedDial}
              onClick={handleSpeedDialOpen}
            >
              {actions.map((action) => {
                return (
                  <SpeedDialAction
                    key={action.name}
                    icon={action.icon}
                    tooltipTitle={action.name}
                    tooltipPlacement='top'
                    onClick={(e) => handleSpeedDialActionClick(e, action.operation)}
                  />
                )
              })}

            </SpeedDial>
            <Grid display='flex' justifyContent='flex-end' alignItems='center' sx={{ height: '56px', ml: 2 }}>
              <Tooltip title={t('cws_app.digital_store.navigation_summary', 'Navigation summary')} placement="top">
                <Button variant='contained'
                  sx={{
                    backgroundColor: '#1a242d',
                    borderRadius: '20px',
                    height: '40px',
                    minWidth: '40px',
                    padding: '0'
                  }}
                  onClick={(e) => handleNavigationSummaryClick(e, 'summary')}
                >
                  <IconComponent iconName={'information-circle'} style={{ fontSize: '20px', color: '#fff' }} />
                </Button>
              </Tooltip>
            </Grid>
          </Box>
        }
      </Grid>
      <StoreMap
        storeMap={storeMap.map_info}
        tasksData={taskData}
        groupingAlertsBy={storeMap.GROUPINGALERTSBY}
        categoriesOnMap={categoriesOnMap}
        visualizationMode={visualizationMode}
        handleCanvasClick={isCLickActive ? handleCanvasClick : () => { }}
        handleCanvasHover={isHoverActive ? handleCanvasHover : () => { }}
        isZoomActive={isZoomActive}
        selectedDots={openTimeline ? poses : currentSessionObstaclesInfo?.poses}
        highlightedBoxes={highlightedBoxes}
        tastType={taskType}
        obstacles={openTimeline ? obstacles : currentSessionObstaclesInfo?.obstacles}
        mapType={mapType}
        hasEnhancedMap={hasEnhancedMap}
      />

      <Box id="highlighter" style={highlightStyle}></Box>
      {!mouseInObs ? (
        <Paper id="tooltipCanvas" sx={{ minWidth: '0', height: '0' }}></Paper>
      ) : (
        selectedObstacleIndex >= 0 && selectedObstacleIndex !== null &&
        <Paper id="tooltipCanvas" sx={{ position: 'absolute', width: '180px', py: 2, px: 2 }}>
          <Grid container alignItems='flex-start'>
            <Grid container item xs={6}>
              <Typography color='textSecondary'>{t('cws_app.Walls.aisle', 'Aisle')}</Typography>
            </Grid>
            <Grid container item xs={6}>
              <Typography>{currentSessionObstaclesInfo?.obstacles[selectedObstacleIndex].aisle}</Typography>
            </Grid>
            <Grid container item xs={6}>
              <Typography color='textSecondary'>{t('cws_app.digital_store.hour', 'Hour')}</Typography>
            </Grid>
            <Grid container item xs={6}>
              <Typography>{currentSessionObstaclesInfo?.obstacles[selectedObstacleIndex].timestamp.split('T')[1]}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {isModalOpen && selectedObstacleIndex !== null ?
        <ObstacleModal
          t={t}
          handleChange={handleObstacleChange}
          getObstacleThumbnails={getObstacleThumbnails}
          isLoadingObstacleThumbnails={isLoadingObstacleThumbnails}
          obstacleThumbnails={obstacleThumbnails}
          setIsModalOpen={setIsModalOpen}
          isModalOpen={isModalOpen}
          value={selectedObstacleIndex}
          currentSessionObstaclesInfo={currentSessionObstaclesInfo}
        />
        : null}
    </Paper>
  );
}
